import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import {
  Edit,
  MailOutline,
  CheckCircle,
  CalendarMonth,
  Business,
  Badge,
  Security,
  LockClock,
  HelpOutline,
  PhotoCamera,
  Apartment,
  DeleteForever,
  InfoOutlined,
} from "@mui/icons-material";
import API from "../../services/api";

// ⬇️ agrega estos imports
import CustomModal from "../../components/CustomModal";
import EditProfileForm from "./EditProfile";           // <— ajusta la ruta si cambió
import ChangePasswordForm from "./ChangePassword";     // <— ajusta la ruta si cambió

/* ================= helpers ================= */
const initials = (fn = "", ln = "", un = "") =>
  ((fn[0] || "") + (ln[0] || "") || (un[0] || "?")).toUpperCase();

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("es-GT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "—";

const SoftTag = ({ text }) => (
  <Box
    sx={{
      px: 1,
      py: 0.25,
      borderRadius: 99,
      border: "1px solid",
      borderColor: "divider",
      fontSize: 12,
      fontWeight: 700,
      color: "text.secondary",
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </Box>
);

const FieldRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
    <Box sx={{ color: "text.secondary", mt: "2px" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  </Stack>
);

/* ============== componente ============== */
export default function UserProfile() {
  // Carga de datos reales
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarInputRef = useRef(null);
  const avatarKey = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const absolutize = (url) => {
    if (!url || typeof url !== "string") return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (process.env.REACT_APP_API_BASE || "http://localhost:8000/").replace(/\/+$/, "");
    return `${base}/${url.replace(/^\/+/, "")}`;
  };
  const compressImage = (file, maxDim = 512, quality = 0.8) => new Promise((resolve) => {
    try {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.onload = () => {
          let { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) return resolve(file);
            const name = (file.name || 'avatar').replace(/\.(png|jpg|jpeg|webp|gif)$/i, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    } catch {
      resolve(file);
    }
  });

  // ⬇️ estados para modales
  const [editOpen, setEditOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("accounts/users/profile/");
      setProfile(data || {});
      const uid = data?.id;
      avatarKey.current = uid ? `user_avatar_${uid}` : null;
      if (data?.avatar) setAvatarPreview(absolutize(data.avatar));
      else if (avatarKey.current) {
        const stored = localStorage.getItem(avatarKey.current);
        if (stored) setAvatarPreview(stored);
      } else {
        setAvatarPreview(null);
      }
    } catch (e) {
      setProfile({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const user = profile || {};

  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.username ||
    "Nombre del usuario";

  // Cálculo de edad de contraseña (0-90)
  const progressDaysRaw = Number.isFinite(user?.password_age_days) ? Number(user.password_age_days) : null;
  const progressDays = progressDaysRaw != null ? progressDaysRaw : (() => {
    const last = new Date(user.password_last_change);
    if (isNaN(last)) return null;
    return Math.max(0, Math.round((Date.now() - last.getTime()) / (1000 * 60 * 60 * 24)));
  })();
  const progressPct = progressDays != null ? Math.min(100, Math.round((progressDays / 90) * 100)) : null;

  // Handlers
  const onEditInfo = () => setEditOpen(true);
  const onChangePassword = () => setPwdOpen(true);
  const onChangeEmail = () => { }; // opcional si lo usarás


  const handlePickAvatar = () => avatarInputRef.current?.click();
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = file.type?.startsWith('image/') ? await compressImage(file) : file;
    const fd = new FormData();
    fd.append('avatar', compressed);
    setUploadingAvatar(true);
    try {
      const { data } = await API.post('accounts/avatar/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data?.avatar || null;
      const finalUrl = absolutize(url);
      setAvatarPreview(finalUrl);
      // persist in userData for navbar popup
      try {
        const stored = JSON.parse(localStorage.getItem('userData')) || {};
        stored.avatar = finalUrl;
        localStorage.setItem('userData', JSON.stringify(stored));
      } catch { }
      await load();
    } catch (err) {
      console.error('Error subiendo avatar', err);
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };
  const handleRemoveAvatar = () => {
    setUploadingAvatar(true);
    API.delete('accounts/avatar/').catch(() => {}).finally(async () => {
      setAvatarPreview(null);
      try {
        const stored = JSON.parse(localStorage.getItem('userData')) || {};
        delete stored.avatar;
        localStorage.setItem('userData', JSON.stringify(stored));
      } catch { }
      await load();
      setUploadingAvatar(false);
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 0 },
        pb: 4,
        maxWidth: 1600,
        mx: "auto",
        minHeight: "100dvh",
      }}
    >
      {/* ================== HEADER ================== */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          mb: 2,
          display: "flex",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 2 }, width: "100%" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ width: "100%", minWidth: 0 }}
          >
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={avatarPreview || undefined}
                sx={{
                  width: 72,
                  height: 72,
                  fontWeight: 800,
                  bgcolor: (t) => t.palette.primary.main,
                  color: (t) => t.palette.primary.contrastText,
                }}
              >
                {!avatarPreview && initials(user?.first_name, user?.last_name, user?.username)}
              </Avatar>
              <Tooltip title="Cambiar foto">
                <IconButton
                  size="small"
                  onClick={handlePickAvatar}
                  sx={{
                    position: "absolute",
                    right: -6,
                    bottom: -6,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                  aria-label="Cambiar foto de perfil"
                >
                  <PhotoCamera fontSize="small" />
                </IconButton>
              </Tooltip>
              {avatarPreview && (
                <Tooltip title="Eliminar foto">
                  <IconButton
                    size="small"
                    onClick={handleRemoveAvatar}
                    sx={{
                      position: "absolute",
                      left: -10,
                      top: -10,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                    aria-label="Eliminar foto de perfil"
                  >
                    <DeleteForever fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarChange}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={fullName}
              >
                {fullName}
              </Typography>

              <Stack
                direction="column"
                spacing={1}
                alignItems="flex-start"
                sx={{ mt: 0.5 }}
                useFlexGap
                flexWrap="wrap"
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {user.role_label}
                </Typography>
                {user.active ? (
                  <Chip
                    size="small"
                    icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    label="Activo"
                    color="success"
                    variant="filled"
                    sx={{ height: 22 }}
                  />
                ) : (
                  <Chip size="small" label="Inactivo" variant="outlined" sx={{ height: 22 }} />
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                sx={{ textTransform: "none", borderRadius: 2 }}
                onClick={onEditInfo}
              >
                Editar
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<MailOutline />}
                sx={{ textTransform: "none", borderRadius: 2 }}
                onClick={onChangeEmail}
              >
                Cambiar correo
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ================== LAYOUT ================== */}
      <Grid2
        container
        spacing={2}
        alignItems="stretch"
        sx={{
          "--RowMin": "260px",
          gridAutoRows: "1fr",
        }}
      >
        {/* ===== Fila 1 ===== */}
        <Grid2 xs={12} md={8}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Información del usuario
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  columnGap: 3,
                  rowGap: 2,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <FieldRow icon={<MailOutline fontSize="small" />} label="Correo electrónico" value={user.email || "—"} />
                <FieldRow icon={<Badge fontSize="small" />} label="Nombre de usuario" value={user.username || "—"} />
                <FieldRow icon={<Business fontSize="small" />} label="Empresa asignada" value={user.company_name || "—"} />
                <FieldRow icon={<CalendarMonth fontSize="small" />} label="Fecha de creación" value={fmtDate(user.created_at)} />

                {/* contacto */}
                <FieldRow icon={<Apartment fontSize="small" />} label="Cargo / Área" value={user.position || "—"} />
                <FieldRow icon={<PhoneIcon fontSize="small" />} label="Teléfono" value={user.phone || "—"} />
              </Box>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Seguridad y acceso
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" icon={<Security sx={{ fontSize: 16 }} />} label="Vigente" color="success" variant="filled" />
                  <SoftTag text={`Último cambio: ${fmtDate(user.password_last_change)}`} />
                </Stack>

                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Edad de la contraseña
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    {progressPct != null ? (
                      <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{
                          height: 8,
                          borderRadius: 99,
                          bgcolor: "action.hover",
                          "& .MuiLinearProgress-bar": { borderRadius: 99 },
                        }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        No disponible
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, width: 140, textAlign: "right" }}>
                    {progressDays != null ? `${progressDays} días / 90 días` : "Sin dato"}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {user.password_policy?.reuse != null && <SoftTag text={`No reutilizar últimas ${user.password_policy.reuse}`} />}
                  {user.password_policy?.minLen != null && <SoftTag text={`Mín. ${user.password_policy.minLen} caracteres`} />}
                  {user.password_policy?.require && <SoftTag text={user.password_policy.require} />}
                </Stack>

                {/* ÚNICO BOTÓN → alineado a la izquierda, tamaño normal */}
                <Box sx={{ pt: 0.5 }}>
                  <Button
                    onClick={onChangePassword}
                    variant="outlined"
                    startIcon={<LockClock />}
                    disabled={uploadingAvatar}
                    sx={{ textTransform: "none", borderRadius: 2, alignSelf: "flex-start" }}
                  >
                    Cambiar ahora
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        {/* Tu cuenta */}
        <Grid2 xs={12} md={6}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Tu cuenta
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <LockClock sx={{ color: "text.secondary" }} fontSize="small" />
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Último acceso
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {fmtDate(user.last_login)}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Completitud de perfil
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <LinearProgress
                        variant="determinate"
                        value={user.profile_score || 0}
                        sx={{
                          height: 8,
                          borderRadius: 99,
                          bgcolor: "action.hover",
                          "& .MuiLinearProgress-bar": { borderRadius: 99 },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, width: 40, textAlign: "right" }}>
                      {user.profile_score || 0}%
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>
        {/* Ayuda */}
        <Grid2 xs={12} md={6}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Ayuda y soporte
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack direction="row" spacing={1.5} alignItems="center">
                <HelpOutline sx={{ color: "text.secondary" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    ¿Necesitas ayuda?
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Podemos enviarte una guía del perfil a tu correo.
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" startIcon={<MailOutline />} sx={{ textTransform: "none", borderRadius: 2 }}>
                  Solicitar guía por correo
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid2>
        {/* ===== Fila 2 ===== */}
        {/* Compañías y roles */}
        <Grid2 xs={12} md={6}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Compañías y roles asignados
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(user.companies || []).map((c) => (
                  <Chip key={c.id} icon={<Business />} label={`${c.name} · ${c.role}`} variant="outlined" sx={{ borderRadius: 2 }} />
                ))}
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Roles aplican por compañía. Contacta a tu administrador para cambios.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid2>

        {/* Permisos efectivos */}
        <Grid2 xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                Permisos efectivos
              </Typography>

              {/* Rol actual + descripción */}
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                Rol actual:{" "}
                <strong>{user.role_label || "—"}</strong>
                {user.role_description ? ` — ${user.role_description}` : ""}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {/* Chips de permisos */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(user.effective_permissions || []).map((p) => {
                  const extraList = user.extra_permissions || [];
                  const isExtra = extraList.includes(p);

                  return (
                    <Chip
                      key={p}
                      icon={<InfoOutlined />}
                      label={p}
                      size="small"
                      variant={isExtra ? "filled" : "outlined"}
                      sx={{
                        borderRadius: 2,
                        ...(isExtra
                          ? {
                            bgcolor: "#F4EED9",
                            color: "#8A6F3D",
                            border: "1px solid #C9A86A",
                            borderColor: "warning.main",
                            fontWeight: 600,
                          }
                          : {
                            color: "text.primary",
                            borderColor: "divider",
                          }),
                      }}
                    />
                  );
                })}
              </Stack>

              {/* Leyenda simple */}
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                <Chip
                  size="small"
                  label="Rol"
                  sx={{ borderRadius: 2, mr: 1, borderColor: "divider" }}
                  variant="outlined"
                />
                Permisos heredados de tu rol.

                <Chip
                  size="small"
                  label="Extra"
                  sx={{
                    ml: 2,
                    borderRadius: 2,
                    bgcolor: "#F4EED9",
                    color: "#8A6F3D",
                    border: "1px solid #C9A86A",
                    fontWeight: 600,
                    mr: 1,
                  }}
                />
                Permisos exclusivos asignados solo a tu usuario.
              </Typography>

            </CardContent>
          </Card>
        </Grid2>


        {/* Sesiones activas */}
        {/* <Grid2 xs={12} md={8}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Sesiones activas
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Logout />}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                  onClick={onCloseAllSessions}
                >
                  Cerrar todas
                </Button>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1} sx={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                {(user.sessions || []).map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      px: 1.25,
                      py: 1,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr auto", sm: "1.2fr 0.9fr auto" },
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <DevicesOther fontSize="small" sx={{ color: "text.secondary" }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={s.device}
                      >
                        {s.device}
                      </Typography>
                    </Stack>

          
                  </Grid2>  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Último: {s.last} · IP {s.ip}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: "none", sm: "flex" } }}>
                        <Place fontSize="inherit" sx={{ color: "text.secondary" }} />
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {s.location}
                        </Typography>
                      </Stack>
                    </Stack>

     
                    <Box sx={{ textAlign: "right" }}>
                      <Tooltip title="Cerrar sesión">
                        <IconButton size="small" sx={{ color: "error.main" }} onClick={() => onCloseSession(s.id)} aria-label={`Cerrar sesión en ${s.device}`}>
                          <Logout fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid2> */}

        {/* Actividad reciente */}
        {/* <Grid2 xs={12} md={4}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Actividad reciente
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1} sx={{ maxHeight: 260, overflowY: "auto" }}>
                {(user.recent_activity || []).map((e) => (
                  <Box key={e.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {e.action}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {e.at} · {e.meta}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid2> */}

      </Grid2>

      {/* ===== Modales ===== */}
      <CustomModal
        open={editOpen}
        setOpen={setEditOpen}
        title="Editar información"
        component={EditProfileForm}
        componentProps={{
          // Usa tus endpoints reales de “me”
          getMePath: "accounts/users/me/",
          updateMePath: "accounts/users/update_me/",
          onSaved: () => load(),
        }}
        width="520px"
      />

      <CustomModal
        open={pwdOpen}
        setOpen={setPwdOpen}
        title="Cambiar contraseña"
        component={ChangePasswordForm}
        componentProps={{
          // Endpoint real de cambio de contraseña
          changePwdPath: "accounts/users/change_password/",
        }}
        width="420px"
      />
    </Box>
  );
}

/* ===== Pequeño ícono faltante ===== */
function PhoneIcon(props) {
  return (
    <svg
      {...props}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="currentColor"
      style={{ display: "inline-block" }}
    >
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.3 22 2 13.7 2 3a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z" />
    </svg>
  );
}
